import { memo, type ReactNode } from 'react'

import { ChatAnimatedItem } from './chat-animated-item.atom'
import type { ChatItemRendererProps, RenderItemParams } from './chat-item-renderer.interface'
import {
  renderAgentReport,
  renderContentBrief,
  renderDelegation,
  renderMessageOrNote,
  renderThinking,
  renderToolCall,
  renderUserInteraction,
} from './chat-item-type-renderers.helper'
import type { ChatItem } from './chat-message.interface'

type ItemRenderer = (params: RenderItemParams, isPinned: boolean) => ReactNode

const itemTypeRenderers: Record<ChatItem['type'], ItemRenderer> = {
  message: renderMessageOrNote,
  note: renderMessageOrNote,
  'tool-call': renderToolCall,
  'content-brief': renderContentBrief,
  'user-interaction': renderUserInteraction,
  thinking: (params) => renderThinking(params),
  'agent-report': renderAgentReport,
  delegation: (params) => renderDelegation(params),
}

const renderItemContent = (params: RenderItemParams): ReactNode => {
  const { item, pinnedItemIdList } = params
  const isPinned = pinnedItemIdList.includes(item.id)
  const renderer = itemTypeRenderers[item.type]
  if (!renderer) return null
  return renderer(params, isPinned)
}

const ChatItemRendererBase: React.FC<ChatItemRendererProps> = ({
  item,
  currentUserId,
  pinnedItemIdList,
  highlightedMessageId,
  onToolCallClick,
  onMessageClick,
  onContentBriefClick,
  onUserInteractionClick,
  onReportClick,
  onPinItem,
  onAttachmentDownload,
  animatedIdsRef,
  isAnimationEnabled,
}): ReactNode => {
  const renderParams: RenderItemParams = {
    item,
    currentUserId,
    pinnedItemIdList,
    highlightedMessageId,
    onToolCallClick,
    onMessageClick,
    onContentBriefClick,
    onUserInteractionClick,
    onReportClick,
    onPinItem,
    onAttachmentDownload,
  }
  const content = renderItemContent(renderParams)
  if (!isAnimationEnabled) return content
  return (
    <ChatAnimatedItem itemId={item.id} animatedIdsRef={animatedIdsRef}>
      {content}
    </ChatAnimatedItem>
  )
}

export const ChatItemRenderer = memo(ChatItemRendererBase)
